import React, { useEffect, useState } from 'react';

import { Button } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import { ReactComponent as RefreshIcon } from '../../assets/images/refresh_icon.svg';
import ErrorHandler from '../../common/components/ErrorHandler';
import Card from '../../components/Card';
import CustomPagination from '../../components/CustomPagination';
import ApplicationFilters from '../../components/Filters/ApplicationFilters';
import PageTitle from '../../components/PageTitle';
import WarningModal from '../../components/WarningModal';
import { t, bareDateTimeFormat } from '../../providers';
import { me as meSlice } from '../AuthenticatedPage/authenticationSlice';
import { SLICE_NAME } from './constants';
import filterItems from './filterItems';
import TableSortHead from './TableSortHead';
import { list, loading as loadingSlice, error as errorSlice, impersonationError } from './usersSlice';
import { UserParserList, filtersParser } from './utils.js';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles((_theme) => ({
    filtersGrid: {
        margin: '30px 0px 40px 0',
    },
    tabs: {
        '& .MuiTabs-scroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'unset',
        },
        '& .MuiTab-root': {
            minWidth: '100px',
        },
    },
    tabContainer: {
        width: '100%',
    },
    cardTable: {
        marginRight: '0px',
        marginTop: '-25px',
        '@media (max-width:960px)': {
            marginRight: 'unset',
        },
    },
    refreshButton: {
        color: '#3F5E20',
        fontSize: '0.75rem',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        textAlign: 'left',
        padding: '0px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
    tableContainer: {
        minHeight: '200px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
        '@media (max-width:960px)': {
            width: 'calc(100vw - 60px)',
        },
    },
    row: {
        minHeight: '4rem',
    },
    name: {
        '&.MuiTableCell-body': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            width: '35%',
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    username: {
        '&.MuiTableCell-body': {
            width: '20%',
            wordBreak: 'break-all',
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            width: '20%',
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    status: {
        width: '10%',
        '&.MuiTableCell-body': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    vatId: {
        '&.MuiTableCell-body': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    parentAccount: {
        '&.MuiTableCell-body': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            width: '20%',
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    currentStatus: {
        '&.MuiTableCell-body': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            width: '20%',
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    userType: {
        '&.MuiTableCell-body': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            width: '20%',
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    citizenId: {
        '&.MuiTableCell-body': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: '133%',
            color: '#5D5D5D',
        },
        '&.MuiTableCell-head': {
            width: '20%',
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    actions: {
        width: '3%',
        '&.MuiTableCell-head': {
            width: '20%',
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '133%',
            color: '#2E2E2E',
        },
    },
    impersonateBtn: {
        background: '#3F5E20',
        boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',
        borderStadius: '2px',
        fontStyle: 'normal',
        fontWeight: '600',
        fontSize: '0.75rem',
        lineHeight: '133%',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: '#FFFFFF',
        minWidth: '110px',
        height: '24px',
        '&:hover': {
            background: '#3F5E20',
            boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',
            borderStadius: '2px',
            fontStyle: 'normal',
            fontWeight: '600',
            fontSize: '0.75rem',
            lineHeight: '133%',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: '#FFFFFF',
            minWidth: '110px',
            height: '24px',
        },
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
            color: '#4F7E1D',
        },
    },
}));

const ASC = 'ASC';
const DESC = 'DESC';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_SORT_COLUMN = 'creationDate';
const DEFAULT_SORT_TYPE = DESC;

const ImpersonateUserPage = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const loading = useSelector(loadingSlice);
    const error = useSelector(errorSlice);
    const impError = useSelector(impersonationError);
    const users = useSelector(list);
    const isTablet = useMediaQuery('(max-width:960px)');

    const { content, totalElements, size, number, lastUpdateDate } = users || {};

    const [sortProps, setSortProps] = useState({ sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE });
    const [usersList, setUsersList] = useState([]);
    const [filtersValues, setFiltersValues] = useState({ searchQuery: '', filters: {}, filtersChips: {} });
    const [requestBodyData, setRequestBodyData] = useState({
        paginationData: {
            page: DEFAULT_PAGE_NUMBER,
            size: DEFAULT_PAGE_SIZE,
        },
        sortingData: {
            sortColumn: sortProps.sortColumn,
            sortType: sortProps.sortType,
        },
        filteringData: filtersParser(filtersValues.filters),
    });
    const [errorDialog, setErrorDialog] = useState({ visibility: false, message: '', title: '' });
    const [errorHandler, setErrorHandler] = useState({ hasRefresh: false, message: '', subtitle: '' });
    const [filterDialog, setFilterDialog] = useState({ visibility: false, message: '' });
    const [hideInfo, setHideInfo] = useState(false);
    const [getFullListIsPressed, setGetFullListIsPressed] = useState(false);

    const toggleInfoMessage = (value) => {
        setHideInfo(value);
    };

    const handleCloseDialog = () => {
        setErrorDialog({ visibility: false, message: '', title: '' });
        dispatch({ type: `${SLICE_NAME}/error`, payload: false });
    };

    const handleCloseFilterDialog = () => {
        setFilterDialog({ visibility: false, message: '' });
        setFiltersValues({ ...filtersValues, filters: {}, filtersChips: {} });
    };

    const handleDateFilter = (idx) => {
        const updatedDateFilter = filtersValues.filters.dates.filter((filter, id) => id !== idx);
        const updatedDateChips = filtersValues.filtersChips.dates.filter((filter, id) => id !== idx);
        let updatedFilter = { ...filtersValues.filters, dates: updatedDateFilter };
        let updatedChips = { ...filtersValues.filtersChips, dates: updatedDateChips };
        if (!updatedDateFilter.length) {
            delete updatedFilter.dates;
        }
        if (!updatedDateChips.length) {
            delete updatedChips.dates;
        }
        setFiltersValues({
            searchQuery: filtersValues.searchQuery,
            filters: updatedFilter,
            filtersChips: updatedChips,
        });
    };

    const handleCancelFilterDialog = () => {
        setFilterDialog({ visibility: false, message: '' });
    };

    const handleChangePage = (event, newPage) => {
        event.preventDefault();
        if (newPage !== number) {
            setRequestBodyData({
                ...requestBodyData,
                paginationData: {
                    page: newPage,
                    size: size,
                },
            });
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const pageSize = event;
        if (size !== pageSize) {
            setRequestBodyData({
                ...requestBodyData,
                paginationData: {
                    page: 0,
                    size: pageSize,
                },
            });
        }
    };

    const clearFilters = () => {
        clearFiltersWarning();
    };

    const clearFiltersWarning = () => {
        setFilterDialog({ visibility: true, message: 'nofilters.warning.text' });
    };

    const requestFullUserList = () => {
        setFiltersValues({ ...filtersValues, searchQuery: '' });
        requestUsers();
    };

    const requestUsers = () => {
        setErrorHandler({ hasRefresh: false, message: '', subtitle: '' });
        setGetFullListIsPressed(true);
        const params = {
            requestBody: requestBodyData,
        };
        dispatch({ type: `${SLICE_NAME}/requestUsers`, payload: { params } });
    };

    useEffect(() => {
        if ((requestBodyData && requestBodyData.searchingData) || getFullListIsPressed) {
            requestUsers();
        } else {
            setUsersList([]);
        }
    }, [dispatch, requestBodyData]);

    useEffect(() => {
        if (filtersValues.searchQuery) {
            setRequestBodyData({
                ...requestBodyData,
                paginationData: { ...requestBodyData.paginationData, page: 0 },
                filteringData: filtersParser(filtersValues.filters),
                searchingData: filtersValues.searchQuery,
            });
        } else {
            const bodyData = { ...requestBodyData };
            delete bodyData['searchingData'];
            setRequestBodyData({
                ...bodyData,
                paginationData: { ...bodyData.paginationData, page: 0 },
                filteringData: filtersParser(filtersValues.filters),
            });
        }
    }, [filtersValues]);

    useEffect(() => {
        const userList = UserParserList(users || []);
        setUsersList(userList);
    }, [users]);

    useEffect(() => {
        if (error && error !== null && error.extraInfo) {
            setErrorHandler({
                message: error.message || 'something.went.wrong',
                hasRefresh: error.extraInfo.hasRefresh || false,
                subtitle: error.extraInfo.secondMessage || '',
            });
        }
        if (impError && impError !== null && impError.extraInfo) {
            setErrorDialog({ visibility: true, message: `${t(impError.message) || ''} ${t(impError.extraInfo.secondMessage) || ''}`, title: '' });
        }
    }, [error, impError]);

    const setSorting = (sortColumn) => {
        const isSameKey = sortColumn === sortProps.sortColumn;
        const oppositeSort = sortProps.sortType === ASC ? DESC : ASC;
        const sortType = !isSameKey ? ASC : oppositeSort;
        setSortProps({ sortColumn, sortType });
        setRequestBodyData({
            ...requestBodyData,
            paginationData: {
                page: DEFAULT_PAGE_NUMBER,
                size: size,
            },
            sortingData: {
                sortColumn: sortColumn,
                sortType: sortType,
            },
        });
    };

    const tableHead = (
        <TableSortHead classes={classes} setSorting={setSorting} sortBy={sortProps.sortColumn} sortValue={sortProps.sortType.toLowerCase()} />
    );

    const requestImpersonation = (userID) => {
        dispatch({ type: `${SLICE_NAME}/requestImpersonation`, payload: { userID: userID } });
    };

    return (
        <div style={{ minHeight: '450px' }}>
            {/* title */}
            <PageTitle title={t('impersonate.user')} details={t('impersonate.user.description')} />
            {/* list */}
            <div className={classes.cardTable}>
                <Card canEdit={false}>
                    {/* filters - search  */}
                    <ApplicationFilters
                        applicationType={'impersonation'}
                        filtersValues={filtersValues}
                        setFiltersValues={setFiltersValues}
                        clearFilters={clearFilters}
                        requestBodyData={requestBodyData}
                        exportApplications={() => {}}
                        filterItems={filterItems}
                        datesOptions={'dateIntervalDraft'}
                        isTablet={isTablet}
                        toggleInfoMessage={toggleInfoMessage}
                        hideInfo={hideInfo}
                        handleDateFilter={handleDateFilter}
                        expoTypes={[]}
                        exportError={false}
                        exportLoading={false}
                        page={'impersonation'}
                        getUserList={requestFullUserList}
                        searchLabel={'which.user.impersonate'}
                        setGetFullListIsPressed={setGetFullListIsPressed}
                        showFilters={(requestBodyData && requestBodyData.searchingData) || getFullListIsPressed}
                        showGetFullListButton={content ? !(content.length > 0 && usersList && usersList.data && !!usersList.data.length) : true}
                    />
                    {errorHandler.message !== '' && (
                        <ErrorHandler
                            title={errorHandler.message}
                            subtitle={errorHandler.subtitle}
                            action={
                                errorHandler.hasRefresh && (
                                    <IconButton
                                        disableFocusRipple
                                        className={classes.refreshButton}
                                        onClick={() => {
                                            requestUsers();
                                        }}>
                                        {t('refresh.button.text')}
                                        <RefreshIcon style={{ paddingLeft: '6.68px' }} aria-hidden="true" focusable="false" />
                                    </IconButton>
                                )
                            }
                        />
                    )}

                    {/** list */}
                    {!loading ? (
                        errorHandler.message === '' &&
                        ((requestBodyData && requestBodyData.searchingData) || getFullListIsPressed) && (
                            <TableContainer tabIndex={0} className={classes.tableContainer}>
                                <Table tabIndex={0} className={classes.table} id="table-impersonation">
                                    {tableHead}
                                    <TableBody>
                                        {(!content || content.length === 0) && (
                                            <TableRow className={classes.row}>
                                                <TableCell colSpan={6} align="center">
                                                    {t('no.impersonate.users')}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {(content || []).map((user, chIdx) => {
                                            const rowClass = classes.row;
                                            return (
                                                <TableRow key={`${user.email}_${chIdx}`} className={rowClass}>
                                                    <TableCell className={classes.username} align="left">
                                                        {user.username}
                                                    </TableCell>
                                                    <TableCell className={classes.name} align="left">
                                                        {user.firstName} {user.lastName}
                                                    </TableCell>
                                                    <TableCell className={classes.vatId} align="left">
                                                        {user.attributes && user.attributes.NIF ? user.attributes.NIF.join(',') : t('not.available')}
                                                    </TableCell>
                                                    <TableCell className={classes.citizenId} align="left">
                                                        {user.attributes && user.attributes.citizenID
                                                            ? user.attributes.citizenID
                                                            : t('not.available')}
                                                    </TableCell>
                                                    <TableCell className={classes.userType} align="left">
                                                        {user.attributes && user.attributes.profile
                                                            ? user.attributes.profile.join(',')
                                                            : t('not.available')}
                                                    </TableCell>
                                                    <TableCell className={classes.currentStatus} align="left">
                                                        {user.enabled ? t('active') : t('inactive')}
                                                    </TableCell>
                                                    <TableCell className={classes.parentAccount} align="left">
                                                        {user.attributes && user.attributes.parentUsername
                                                            ? user.attributes.parentUsername
                                                            : t('not.available')}
                                                    </TableCell>
                                                    <TableCell className={classes.actions} align="left">
                                                        <Button
                                                            aria-label={t('impersonate.user')}
                                                            id={'impersonateButton'}
                                                            onClick={() => {
                                                                requestImpersonation(user.id);
                                                            }}
                                                            disableRipple
                                                            disableFocusRipple
                                                            className={classes.impersonateBtn}>
                                                            {t('impersonate')}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress aria-hidden="true" />
                            <Typography variant="srOnly" role="alert">
                                {t('loading.text')}
                            </Typography>
                        </Box>
                    )}
                </Card>
            </div>
            {/* pagination */}
            {!!content && errorHandler.message === '' && ((requestBodyData && requestBodyData.searchingData) || getFullListIsPressed) && (
                <CustomPagination
                    content={content}
                    defaultPageSize={DEFAULT_PAGE_SIZE}
                    totalElements={totalElements}
                    component={'div'}
                    count={totalElements || 0}
                    size={size || DEFAULT_PAGE_SIZE}
                    page={number || DEFAULT_PAGE_NUMBER}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    selectProps={{ renderValue: (value) => `${value} ${t('items')}` }}
                    paginationId={'impersonation'}
                    isTablet={isTablet}
                    lastUpdateDate={lastUpdateDate}
                />
            )}
            {errorDialog.visibility && (
                <WarningModal
                    visibility={errorDialog.visibility}
                    message={errorDialog.message}
                    onConfirmation={handleCloseDialog}
                    confirmButtonText={t('export.unavailable.button')}
                    title={errorDialog.title}
                />
            )}
            {filterDialog.visibility && (
                <WarningModal
                    visibility={filterDialog.visibility}
                    message={t(filterDialog.message)}
                    onConfirmation={handleCancelFilterDialog}
                    confirmButtonText={t('cancel')}
                    cancelButtonText={t('continue')}
                    onCancel={handleCloseFilterDialog}
                />
            )}
        </div>
    );
};

export default ImpersonateUserPage;
